/**
 * Simple File Service
 * Handles file uploads and management
 */

import { apiService } from './ApiService';
import { trackEvent } from './usageMetrics';

export interface FileUploadProgress {
  assetId: string;
  progress: number;
  status: 'uploading' | 'processing' | 'converting' | 'completed' | 'error';
  message: string;
  fileId?: string;  // For tracking conversion status
}

export class FileService {
  private async refreshUploadToken(): Promise<string | null> {
    // The app's one shared renewal (audit bug 81): a refusal signs out there, a server that cannot
    // be reached signs nobody out. Without it (no AuthContext) the old way below.
    if (apiService.hasSessionRenewer()) {
      const renewal = await apiService.renewSession();
      return 'token' in renewal ? renewal.token : null;
    }
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      // A 401 here means the session is expired - trigger the global logout flow
      if (response.status === 401) {
        apiService.notifyUnauthorized();
      }
      return null;
    }

    const data = await response.json();
    return data.accessToken || null;
  }

  async uploadFile(
    assetId: string,
    file: File,
    fileType: string,
    onProgress?: (progress: FileUploadProgress) => void,
    companionFiles?: File[]
  ): Promise<any> {


    onProgress?.({
      assetId,
      progress: 0,
      status: 'uploading',
      message: 'Starting file upload...'
    });

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);
      formData.append('assetId', assetId);

      // Add companion files if provided
      if (companionFiles && companionFiles.length > 0) {
        companionFiles.forEach((companionFile, index) => {
          formData.append(`companionFile_${index}`, companionFile);
        });
      }


      onProgress?.({
        assetId,
        progress: 25,
        status: 'uploading',
        message: 'Uploading file...'
      });

      // Send to API server with enhanced progress tracking
      const url = `/api/assets/${assetId}/files`;

      onProgress?.({
        assetId,
        progress: 25,
        status: 'uploading',
        message: 'Refreshing upload authorization...'
      });

      const freshToken = await this.refreshUploadToken();
      if (!freshToken) {
        throw new Error('Authentication required. Please login again.');
      }

      // Create XMLHttpRequest for upload progress tracking
      const response = await new Promise<Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const uploadProgress = Math.round((event.loaded / event.total) * 70); // Reserve 30% for processing
            onProgress?.({
              assetId,
              progress: uploadProgress,
              status: 'uploading',
              message: fileType === 'e57'
                ? `Uploading E57 file... ${uploadProgress}%`
                : `Uploading ${fileType.toUpperCase()} file... ${uploadProgress}%`
            });
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            trackEvent('file.upload', { assetId, props: { type: fileType, sizeKB: Math.round(file.size / 1024) } });
          }
          onProgress?.({
            assetId,
            progress: 70,
            status: 'processing',
            message: fileType === 'e57'
              ? 'Extracting panoramic images from E57 file...'
              : 'Processing upload...'
          });

          const response = new Response(xhr.responseText, {
            status: xhr.status,
            statusText: xhr.statusText,
            headers: new Headers({
              'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json'
            })
          });
          resolve(response);
        };

        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.onabort = () => reject(new Error('Upload aborted'));

        xhr.open('POST', url);
        xhr.setRequestHeader('Authorization', `Bearer ${freshToken}`);
        xhr.send(formData);
      });


      // Handle 401 Unauthorized - trigger the global logout flow (same as ApiService)
      if (response.status === 401) {
        apiService.notifyUnauthorized();
        throw new Error('Authentication required. Please login again.');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ RESPONSE ERROR:', errorText);
        throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();

      onProgress?.({
        assetId,
        progress: 100,
        status: 'completed',
        message: 'File uploaded successfully'
      });

      return result;

    } catch (error) {
      console.error('❌ File upload failed:', error);

      onProgress?.({
        assetId,
        progress: 0,
        status: 'error',
        message: 'File upload failed'
      });

      throw error;
    }
  }

  async deleteFile(assetId: string, fileId: string): Promise<boolean> {
    try {
      await apiService.delete(`/assets/${assetId}/files/${fileId}`);

      // Note: DXF deletion is now handled on the server side when DWG is deleted
      return true;
    } catch (error) {
      console.error('❌ File deletion failed:', error);
      return false;
    }
  }

  // Set the default file for an asset (e.g., default IFC file)
  async setDefaultFile(assetId: string, fileId: string): Promise<boolean> {
    try {
      await apiService.post(`/assets/${assetId}/files/${fileId}/set-default`, {});
      return true;
    } catch (error) {
      console.error('❌ Failed to set default IFC file:', error);
      return false;
    }
  }

  async getFileUrl(assetId: string, fileId: string): Promise<string | null> {
    try {
      // TODO: Return URL to file in /data/assets/{assetId}/ folder
      return `/data/assets/${assetId}/files/${fileId}`;
    } catch (error) {
      console.error('❌ Failed to get file URL:', error);
      return null;
    }
  }

  // Simple panorama extraction (replacing complex E57 processing)
  async extractPanoramas(
    file: File,
    onProgress?: (progress: FileUploadProgress) => void
  ): Promise<string[]> {

    onProgress?.({
      assetId: '',
      progress: 0,
      status: 'processing',
      message: 'Starting panorama extraction...'
    });

    try {
      // TODO: Implement basic panorama extraction
      // For now, return empty array

      onProgress?.({
        assetId: '',
        progress: 100,
        status: 'completed',
        message: 'Panorama extraction completed'
      });

      return [];

    } catch (error) {
      onProgress?.({
        assetId: '',
        progress: 0,
        status: 'error',
        message: 'Panorama extraction failed'
      });

      console.error('❌ Panorama extraction failed:', error);
      throw error;
    }
  }

  // Helper methods
  private async simulateUpload(
    assetId: string,
    onProgress?: (progress: FileUploadProgress) => void
  ): Promise<void> {
    const steps = [20, 40, 60, 80, 95];

    for (const progress of steps) {
      await new Promise(resolve => setTimeout(resolve, 200));
      onProgress?.({
        assetId,
        progress,
        status: 'uploading',
        message: `Uploading... ${progress}%`
      });
    }
  }
}

// Singleton instance
export const fileService = new FileService();
